import { functions } from 'firebaseSDK';
import { getFirestore, setDoc, doc, Timestamp } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { TenancyStatusTypes } from '../../config/types';
import Actions from './abstract/Actions';

class TenancyActions extends Actions {
  terminateTenancy(tenancyDoc) {
    return tenancyDoc
      .update({
        status: TenancyStatusTypes.terminated,
        dateUpdated: Timestamp.fromDate(new Date()),
      })
      .then(() => {
        functions.tenancy
          .terminateTenancy({ tenancyUid: tenancyDoc.id })
          .catch((e) => console.warn('Failed to call terminateTenancy'));
      });
  }

  preSubmitTenant_(data) {
    const tenantData = { ...data };

    tenantData.dateUpdated = Timestamp.fromDate(new Date());
    tenantData.fullName = [
      tenantData.title,
      tenantData.firstName,
      tenantData.lastName,
    ]
      .filter(Boolean)
      .join(' ');

    if (tenantData.hasGuarantor) {
      tenantData.guarantor = {
        ...data.guarantor,
      };

      tenantData.guarantor.fullName = [
        tenantData.guarantor.title,
        tenantData.guarantor.firstName,
        tenantData.guarantor.lastName,
      ]
        .filter(Boolean)
        .join(' ');
    }

    return tenantData;
  }

  editTenant(tenantDoc, data) {
    const tenantData = this.preSubmitTenant_({
      ...tenantDoc.data,
      ...data,
    });

    return tenantDoc.update(tenantData);
  }

  async createTenant(tenancyUid, tenantData, propertyDoc) {
    const data = this.preSubmitTenant_(tenantData);
    const timestamp = Timestamp.fromDate(new Date());

    data.tenantUid = uuidv4().toUpperCase();
    data.tenancyUid = tenancyUid;
    data.linkedDate = timestamp;
    data.payments.depositPaidDate = timestamp;
    data.payments.standingOrderSetupDate = timestamp;
    data.linkedDate = timestamp;
    data.linkingCode = `${propertyDoc.data.address.postcode}${data.tenantUid
      .slice(0, 8)
      .replaceAll(' ', '')}`;
    data.guarantor.tenancyAgreementDateSigned = timestamp;

    return setDoc(
      doc(getFirestore(), `/tenancies/${tenancyUid}/tenants/${data.tenantUid}`),
      data
    );
  }

  createAndSendAgreement(tenancyDoc, isPreview) {
    return functions.agreement.createAndSend({
      tenancyUid: tenancyDoc.id,
      isPreview,
    });
  }

  sendLinkToTenants(tenancyDoc) {
    return functions.tenancy.linkTenants({
      tenancyUid: tenancyDoc.id,
    });
  }

  sendDepositReminder(tenancyDoc, paymentType) {
    return functions.tenancy.sendPaymentReminder({
      tenancyUid: tenancyDoc.id,
      paymentType,
    });
  }

  registerDeposit(tenancyDoc) {
    return functions.deposit.register({
      tenancyUid: tenancyDoc.id,
    });
  }

  markAsOccupied(tenancyDoc) {
    return tenancyDoc.update({
      propertyIsOccupied: true,
      dateUpdated: Timestamp.fromDate(new Date()),
    });
  }

  async createTenancy(propertyDoc, data, tenantsData = []) {
    const { profile } = this.root.userDataState;
    const tenancyData = { ...data };
    const timestampDate = Timestamp.fromDate(new Date());

    console.log('createTenancy', data);

    tenancyData.tenancyUid = uuidv4().toUpperCase();

    // Fill up the current date into all the date fields
    tenancyData.dateUpdated = timestampDate;
    tenancyData.dateCreated = timestampDate;
    tenancyData.agreement.dateCreated = timestampDate;
    tenancyData.agreement.dateUpdated = timestampDate;

    // Assign relations data
    tenancyData.landlordUid = profile.data.profileUid;
    tenancyData.landlordEmail = profile.data.emailAddress;
    tenancyData.propertyUid = propertyDoc.id;

    tenancyData.clauses = propertyDoc.data.clauses;
    tenancyData.otherCharges = propertyDoc.data.otherCharges;

    // Deposit due date calculation
    const depositDueDate = new Date(tenancyData.dates.startDate);
    depositDueDate.setDate(depositDueDate.getDate() - 15);
    tenancyData.dates.depositDue = Timestamp.fromDate(depositDueDate);

    // Deposit rent due date calculation
    const depositRentDue = new Date(tenancyData.dates.startDate);
    depositRentDue.setMonth(
      depositRentDue.getMonth() + tenancyData.durationInMonths,
      depositRentDue.getDate() + 10
    );
    tenancyData.dates.depositReturnDue = Timestamp.fromDate(depositRentDue);

    // Fill the rent due dates
    tenancyData.dates.rentDueDates = new Array(tenancyData.durationInMonths)
      .fill(0)
      .map((i, idx) => {
        const date = new Date(tenancyData.dates.startDate);
        date.setMonth(date.getMonth() + idx);
        return Timestamp.fromDate(date);
      });

    // Set end date
    const endDate = new Date(tenancyData.dates.startDate);
    endDate.setMonth(endDate.getMonth() + tenancyData.durationInMonths);
    tenancyData.dates.endDate = Timestamp.fromDate(endDate);

    // Finally, Convert start date to swift date
    tenancyData.dates.startDate = Timestamp.fromDate(
      tenancyData.dates.startDate
    );

    const uniqueReference = tenancyData.tenancyUid.slice(0, 6);
    // Assign deposit reference
    tenancyData.payments.depositReference = `PD${uniqueReference}${uuidv4()
      .toUpperCase()
      .slice(0, 4)}`;
    // Assign rent reference
    tenancyData.payments.rentReference = `PR${uniqueReference}${uuidv4()
      .toUpperCase()
      .slice(0, 4)}`;

    tenancyData.tenantEmails = tenantsData.map(
      ({ emailAddress }) => emailAddress
    );
    tenancyData.tenantGuarantorEmails = tenantsData
      .map(({ hasGuarantor, guarantor }) =>
        hasGuarantor ? guarantor.emailAddress : null
      )
      .filter(Boolean);

    tenancyData.tenantCount = tenancyData.tenantEmails.length;

    // Convert to numbers
    tenancyData.payments.depositAmount *= 1;
    tenancyData.payments.rentAmount *= 1;

    // tenancyData.payments.standingOrderAmount = tenancyData.payments.rentAmount;

    console.log('createTenancy final data', tenancyData);
    // First, create tenancy
    await setDoc(
      doc(getFirestore(), `tenancies/${tenancyData.tenancyUid}`),
      tenancyData
    );

    // Create tenants
    await Promise.all(
      tenantsData.map((tenantData, idx) =>
        this.createTenant(
          tenancyData.tenancyUid,
          {
            ...tenantData,
            tenantOrder: idx,
          },
          propertyDoc
        )
      )
    );

    await propertyDoc.update({
      tenancyUids: [tenancyData.tenancyUid, ...propertyDoc.data.tenancyUids],
    });
  }
}

export default TenancyActions;
